import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input, SkeletonList } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { FilterToolbar } from "@/components/layout/FilterToolbar";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatDateInTz } from "@/design-system/lib/format";
import { portalMaterialsRecipientApi } from "../api/materials";
import { brokerApi } from "../api/broker";
import { PortalApiError } from "../api/client";
import { usePortalAuth } from "../PortalAuthContext";
import type { PortalMaterialDto } from "../api/types";
import { MaterialPreviewDrawer } from "../components/MaterialPreviewDrawer";
import { BrokerLockedNotice } from "../components/BrokerLockedNotice";

// RECON→FIX (UAT 2026-05-30) — single source of the broker-library gate
// copy. Mirrors the server's BrokerGateMessage so the locked banner and a
// defensive 403 surface read identically.
const GATE_MESSAGE =
  "You will gain access to this once you have completed your application and been certified.";

// Post-Rollout PR 2 (PROB-20) — Files tab surfaces audience-tagged
// PortalMaterials (the admin Library) so a Seller can find their
// Seller-tagged "Active" reference docs without staff having to email
// the link. Broker materials route through /api/portal/broker/materials
// (certification-gated); Buyer / Seller route through
// /api/portal/materials (audience-filtered by the session's AccountType).
//
// The Documents (filed/signed/uploaded) surface stays as its own tab —
// folding it into Files would have ballooned this PR. Section 1
// (Documents) is therefore intentionally absent here.

export function FilesTab() {
  const auth = usePortalAuth();
  const isBroker = auth.me?.kind === "Broker";
  // fix-broker-portal — an uncertified broker can't reach the library yet;
  // the broker endpoints would 403 ("Broker session required.") on a
  // freshly self-healed session. Short-circuit to the friendly onboarding
  // notice and never call the gated endpoint.
  const brokerLocked = isBroker && auth.me?.isCertified !== true;

  const [rows, setRows] = useState<PortalMaterialDto[] | null>(null);
  // FIND-25 — the broker materials endpoint returns `{ materials, gated }`;
  // `gated` is true until the broker is certified. For recipients there is
  // no gating, so it stays false.
  const [gated, setGated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState<PortalMaterialDto | null>(null);

  const fetch = useCallback(async () => {
    try {
      if (isBroker) {
        const res = await brokerApi.listMaterials();
        setRows(res.materials);
        setGated(res.gated);
      } else {
        setRows(await portalMaterialsRecipientApi.list());
        setGated(false);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the library.");
    }
  }, [isBroker]);

  // FIND-24 — do nothing until auth is hydrated. Before `auth.me` resolves
  // `isBroker` is false, so an actual broker would hit the recipient
  // endpoint (no recipient scope → 403). Wait for /me, then fetch with the
  // correct `isBroker`.
  useEffect(() => {
    if (auth.loading || !auth.me || brokerLocked) return;
    void fetch();
  }, [auth.loading, auth.me, brokerLocked, fetch]);

  const download = useCallback(
    async (id: string) => {
      // FIND-25 — while gated, downloads 404/403 server-side; don't even try.
      if (gated) return;
      try {
        const dl = isBroker
          ? await brokerApi.downloadMaterial(id)
          : await portalMaterialsRecipientApi.download(id);
        window.open(dl.url, "_blank", "noopener,noreferrer");
      } catch (err) {
        // Defense-in-depth — the server enforces the gate and returns 403
        // with the gate message even if the UI's `gated` flag is stale.
        if (err instanceof PortalApiError && err.status === 403) {
          setError(err.errors[0] ?? GATE_MESSAGE);
          return;
        }
        setError(err instanceof Error ? err.message : "Couldn't open that file.");
      }
    },
    [isBroker, gated],
  );

  const filtered = useMemo(() => {
    if (!rows) return null;
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      r.title.toLowerCase().includes(term) ||
      (r.description ?? "").toLowerCase().includes(term) ||
      (r.category ?? "").toLowerCase().includes(term),
    );
  }, [rows, search]);

  if (brokerLocked) {
    return (
      <BrokerLockedNotice
        title="Files & library"
        description="The broker materials library — pitch decks, guides, and reference documents — will be available here once you're certified."
      />
    );
  }
  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return (
      <SkeletonList data-testid="files-loading" />
    );
  }

  return (
    <section aria-label="Files & library" className="flex flex-col gap-space-4">
      {/* FIND-25 — broker materials are locked until certified; mirror the
          BrokerMaterialsPage banner + disable the downloads below. */}
      {gated && (
        <div
          className="p-space-4 border border-border-light rounded-md bg-white-warm/40"
          data-testid="materials-locked"
        >
          <p className="text-small text-onyx m-0">{GATE_MESSAGE}</p>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Finish the onboarding course to unlock the broker materials library.{" "}
            <Link to="/portal/broker/course" className="underline">
              Continue onboarding
            </Link>
          </p>
        </div>
      )}

      {rows.length === 0 ? (
        <p
          className="text-small text-gray-mid"
          data-testid="files-empty"
        >
          No materials published for your role yet.
        </p>
      ) : (
        <>
          <FilterToolbar
            growSearch
            searchInput={
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, category, or description…"
                data-testid="files-search"
              />
            }
          />
          <div data-testid="files-list">
            <DataTable<PortalMaterialDto>
              rows={filtered ?? []}
              rowKey={(r) => r.id}
              sortable
              defaultSort={{ key: "title", dir: "asc" }}
              groupBy={(r) => r.category ?? "Uncategorized"}
              pagination={{ pageSize: 25 }}
              emptyMessage="No files match that search."
              columns={filesColumns(download, gated)}
              onRowClick={(material) => setSelectedMaterial(material)}
            />
          </div>
        </>
      )}

      <div className="mt-space-4 p-space-4 border border-border-light rounded-md bg-white-warm/40">
        <h3 className="text-h5 font-display text-onyx m-0">
          Policies & disclosures
        </h3>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          No policies published for your role yet.
        </p>
      </div>

      <MaterialPreviewDrawer
        open={!!selectedMaterial}
        material={selectedMaterial}
        gated={gated}
        gateMessage={GATE_MESSAGE}
        previewUrl={selectedMaterial && isBroker ? brokerApi.materialPreviewUrl(selectedMaterial.id) : null}
        onClose={() => setSelectedMaterial(null)}
        onDownload={download}
      />
    </section>
  );
}

function filesColumns(
  onDownload: (id: string) => void,
  gated: boolean,
): Column<PortalMaterialDto>[] {
  return [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortAccessor: (m) => m.title,
      render: (m) => (
        <span
          className="text-small font-medium text-onyx"
          data-testid={`file-row-${m.id}`}
        >
          {m.title}
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
      mobileHidden: true,
      render: (m) => (
        <span className="text-caption text-gray-mid line-clamp-2">
          {m.description ?? "—"}
        </span>
      ),
    },
    {
      key: "size",
      header: "Size",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (m) => m.sizeBytes,
      render: (m) => (
        <span className="text-caption text-gray-mid">{formatBytes(m.sizeBytes)}</span>
      ),
    },
    {
      key: "published",
      header: "Published",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (m) => Date.parse(m.createdAt),
      render: (m) => <PublishedCell at={m.createdAt} />,
    },
    {
      key: "action",
      header: "Action",
      render: (m) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDownload(m.id);
          }}
          disabled={gated}
          // Negative margins offset the padding so the visual layout is
          // unchanged while the touch target grows to ~44px.
          className="text-small font-medium underline text-onyx hover:text-gold-shadow disabled:text-gray-mid disabled:no-underline py-space-2 -my-space-2 px-space-2 -mx-space-2"
          data-testid={`file-download-${m.id}`}
        >
          {gated ? "Locked" : "Download"}
        </button>
      ),
    },
  ];
}

function PublishedCell({ at }: { at: string }) {
  return <span className="text-caption text-gray-mid">{formatDateInTz(at, null)}</span>;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
